<template>
  <div v-if="lesson" class="page stack">
    <section>
      <p class="eyebrow">Lesson {{ lesson.number }} · {{ lesson.technique }}</p>
      <h1>{{ lesson.title }}</h1>
      <p class="dish">{{ lesson.dish }} <span class="muted">with {{ lesson.side.toLowerCase() }}</span></p>
      <p class="muted">{{ lesson.why }}</p>
      <p class="muted small">Builds on: {{ lesson.buildsOn }} · {{ formatMinutes(lesson.minutes) }}<span v-if="lesson.weekend"> · needs a weekend afternoon</span></p>
      <p v-if="lesson.nightBefore" class="night-before"><strong>The night before:</strong> {{ lesson.nightBefore }}</p>
    </section>

    <section class="card">
      <p class="eyebrow">When</p>
      <p v-if="entry.plannedFor"><strong>{{ shortDate(entry.plannedFor) }}</strong> · on Meal Hat’s schedule as “Lesson {{ lesson.number }}”.</p>
      <p v-else class="muted">Not on a night yet. Planning happens by the week.</p>
      <router-link to="/week" class="btn btn-sm">The week: nights, order, groceries</router-link>
    </section>

    <section class="card">
      <p class="eyebrow">This lesson asks for</p>
      <ul class="plain-list">
        <li v-for="item in lesson.order" :key="item.creameryId" class="order-row">
          <div class="order-row__text">
            <a :href="itemUrl(item.creameryId, item.name)" target="_blank" rel="noopener" class="order-row__name">{{ item.name }}</a>
            <span class="muted small">{{ item.qty }}<span v-if="catalog[item.creameryId]"> · {{ priceLine(catalog[item.creameryId]) }}</span> · from the creamery</span>
            <span v-if="item.note" class="muted small order-row__note">{{ item.note }}</span>
          </div>
        </li>
        <li v-for="ing in lesson.grocery" :key="ing.name" class="order-row">
          <span>{{ ing.name }}<span v-if="ing.qty" class="muted"> · {{ ing.qty }}</span><span v-if="ing.pantry" class="muted small"> · pantry</span></span>
        </li>
      </ul>
      <p class="muted small">The fridge check and the shopping list are on the week page, across every lesson at once.</p>
    </section>

    <section class="card">
      <p class="eyebrow">You’ll need</p>
      <ul class="plain-list bullets"><li v-for="tool in lesson.equipment" :key="tool">{{ tool }}</li></ul>
      <p class="muted small">Based on {{ lesson.source.name }}<template v-if="lesson.source.url">. <a :href="lesson.source.url" target="_blank" rel="noopener">The original</a></template>.</p>
    </section>

    <div class="row">
      <router-link :to="`/cook/${lesson.id}`" class="btn btn-primary btn-lg">It’s time to cook</router-link>
      <router-link v-if="entry.cookedAt && !entry.debrief" :to="`/debrief/${lesson.id}`" class="btn btn-lg">We ate. Talk.</router-link>
    </div>
    <p v-if="entry.debrief?.summary" class="muted">Cooked {{ shortDate(entry.cookedAt) }}. {{ entry.debrief.summary.verdict }}</p>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { lessonById, formatMinutes } from '../curriculum/lessons.js';
import { loadCatalog, itemUrl, priceLine } from '../utils/creamery.js';
import { loadProgress, shortDate } from '../utils/course.js';
import { setActivity } from '../state.js';

export default {
  name: 'LessonScreen',
  props: { id: { type: String, required: true }, hat: { type: String, default: null } },
  setup (props) {
    const lesson = computed(() => lessonById(props.id));
    const entry = ref({});
    const catalog = ref({});
    setActivity('lesson');

    async function load () {
      if (!props.hat || !lesson.value) return;
      const progress = await loadProgress(props.hat);
      entry.value = progress[lesson.value.id] || {};
    }
    watch(() => [props.hat, props.id], load, { immediate: true });
    loadCatalog().then((items) => { catalog.value = items; return items; }).catch(() => {});

    return { lesson, entry, catalog, formatMinutes, shortDate, itemUrl, priceLine };
  }
};
</script>

<style scoped>
.dish { font-size: 1.15rem; font-weight: 600; }
.small { font-size: 0.85rem; }
.night-before { background: var(--accent-soft); border-radius: var(--radius); padding: 0.6rem 0.9rem; }
.date-label { display: flex; flex-direction: column; gap: 0.25rem; font-size: 0.85rem; color: var(--muted); }
.date-input { font: inherit; font-size: 1rem; padding: 0.4rem 0.6rem; border: 1px solid var(--line); border-radius: 10px; background: var(--surface); color: var(--text); }
.plain-list { list-style: none; margin: 0 0 0.75rem; padding: 0; display: flex; flex-direction: column; gap: 0.5rem; }
.plain-list.bullets { list-style: disc; padding-left: 1.2rem; }
.order-row__text { display: flex; flex-direction: column; }
.order-row__name { font-weight: 600; }
.order-row__note { font-style: italic; }
.ing-row { display: flex; gap: 0.6rem; align-items: flex-start; }
.ing-row__dot { width: 0.7rem; height: 0.7rem; border-radius: 50%; margin-top: 0.45rem; flex: 0 0 auto; background: var(--accent); }
.ing-row__dot--have { background: var(--green); }
.ing-row__dot--listed { background: #b58a2c; }
.ing-row__dot--pantry { background: #cfc6b8; }
.ing-row__text { display: flex; flex-direction: column; }
</style>
